import { useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Layout from "./components/Layout";
import { IdEncoderProvider } from "./context/IdEncoderContext";
import { GlobalMenuProvider } from "./context/GlobalMenuContext";
import { UserProvider } from "../src/context/UserContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import "./App.css";

// Lazy Loaded Components
const ActivityLogPage = lazy(
  () => import("./pages/activity-log/ActivityLogPage"),
);
const Home = lazy(() => import("./pages/Home/Home"));
const Lists = lazy(() => import("./pages/projects/lists/Lists"));
const ProjectCreation = lazy(
  () => import("./pages/projects/create/ProjectCreation"),
);
const ProjectView = lazy(() => import("./pages/projects/view/ProjectView"));
const ProjectOverview = lazy(
  () => import("./pages/projects/overview/ProjectOverview"),
);
const Login = lazy(() => import("./pages/Auth/Login"));
const ForgotPassword = lazy(() => import("./pages/Auth/ForgotPassword"));
const ProjectMappingLists = lazy(
  () => import("./pages/project-labour-mapping/lists/Lists"),
);
const ProjectMappingCreate = lazy(
  () => import("./pages/project-labour-mapping/create/ProjectMapping"),
);
const ProjectMappingViewPage = lazy(
  () => import("./pages/project-labour-mapping/view/ProjectMappingViewPage"),
);
const LabourTypeCreate = lazy(
  () => import("./pages/labour-type/create/Create"),
);
const LabourTypeLists = lazy(() => import("./pages/labour-type/lists/Lists"));
const LabourTypeView = lazy(
  () => import("./pages/labour-type/view/LabourTypeView"),
);
const ContractorCreation = lazy(
  () => import("./pages/contractor/create/Create"),
);
const ContractorList = lazy(() => import("./pages/contractor/lists/Lists"));
const ContractorView = lazy(
  () => import("./pages/contractor/view/ContractorView"),
);
const AttendanceCreation = lazy(
  () => import("./pages/attendance/create/Create"),
);
const AttendanceLists = lazy(() => import("./pages/attendance/lists/Lists"));
const NMRAttendanceCreation = lazy(
  () => import("./pages/attendance/nmr-create/NmrCreate"),
);
const AttendanceView = lazy(
  () => import("./pages/attendance/view/AttendanceView"),
);
const LabourWagesCreation = lazy(
  () => import("./pages/labour_wages/create/Create"),
);
const LabourWagesLists = lazy(() => import("./pages/labour_wages/lists/Lists"));
const LabourWagesView = lazy(() => import("./pages/labour_wages/view/View"));
const QuotationCreation = lazy(() => import("./pages/quotation/create/Create"));
const QuotationLists = lazy(() => import("./pages/quotation/lists/Lists"));
const QuotationView = lazy(
  () => import("./pages/quotation/view/QuotationView"),
);
const CustomerCreation = lazy(() => import("./pages/customers/create/Create"));
const CustomerLists = lazy(() => import("./pages/customers/lists/Lists"));
const CustomerView = lazy(() => import("./pages/customers/view/CustomerView"));
const EstimationCreation = lazy(
  () => import("./pages/estimation/create/Create"),
);
const BBSCreate = lazy(() => import("./pages/estimation/create/BBSCreate"));
const BBSView = lazy(() => import("./pages/estimation/view/BBSView"));
const BBSEdit = lazy(() => import("./pages/estimation/edit/BBSEdit"));
const EstimationEdit = lazy(() => import("./pages/estimation/edit/Edit"));
const EstimationLists = lazy(() => import("./pages/estimation/lists/Lists"));
const EstimationView = lazy(() => import("./pages/estimation/view/View"));
const GroupLists = lazy(() => import("./pages/groups/lists/Lists"));
const LedgerCreation = lazy(() => import("./pages/ledger/create/Create"));
const LedgerLists = lazy(() => import("./pages/ledger/lists/Lists"));
const LedgerViewPage = lazy(() => import("./pages/ledger/view/LedgerView"));
const CashbookLists = lazy(() => import("./pages/cashbook/lists/Lists"));
const CashbookCreate = lazy(() => import("./pages/cashbook/create/Create"));
const CashbookView = lazy(() => import("./pages/cashbook/view/View"));
const BankbookLists = lazy(() => import("./pages/bankbook/lists/Lists"));
const BankbookCreation = lazy(() => import("./pages/bankbook/create/Create"));
const BankbookView = lazy(() => import("./pages/bankbook/view/View"));
const JournalLists = lazy(() => import("./pages/journal/lists/Lists"));
const JournalCreate = lazy(() => import("./pages/journal/create/Create"));
const JournalView = lazy(() => import("./pages/journal/view/View"));
const VoucherLists = lazy(() => import("./pages/vouchers/lists/Lists"));
const VoucherCreate = lazy(() => import("./pages/vouchers/create/Create"));
const VoucherView = lazy(() => import("./pages/vouchers/view/View"));
const PaymentCreation = lazy(() => import("./pages/payments/create/Create"));
const PaymentLists = lazy(() => import("./pages/payments/lists/Lists"));
const PaymentView = lazy(() => import("./pages/payments/view/View"));
const LookupMasterList = lazy(
  () => import("./pages/lookups/LookupMasterLists"),
);
const LookupValueList = lazy(() => import("./pages/lookups/LookupValueList"));
const ReceiptLists = lazy(() => import("./pages/receipts/lists/Lists"));
const ReceiptCreate = lazy(() => import("./pages/receipts/create/Create"));
const ReceiptView = lazy(() => import("./pages/receipts/view/ReceiptView"));
const InvoiceCreation = lazy(() => import("./pages/invoice/create/Create"));
const InvoiceLists = lazy(() => import("./pages/invoice/lists/List"));
const InvoiceView = lazy(() => import("./pages/invoice/view/InvoiceView"));
const ProductCategoryCreation = lazy(
  () => import("./pages/product-category/create/Create"),
);
const ProductCategoryList = lazy(
  () => import("./pages/product-category/lists/List"),
);
const ProductCategoryView = lazy(
  () => import("./pages/product-category/view/View"),
);
const UomList = lazy(() => import("./pages/uom/lists/List"));
const UomCreate = lazy(() => import("./pages/uom/create/Create"));
const UomView = lazy(() => import("./pages/uom/view/view"));
const ProductCreation = lazy(() => import("./pages/products/create/Create"));
const ProductList = lazy(() => import("./pages/products/lists/List"));
const ProductView = lazy(() => import("./pages/products/view/View"));
const VendorCreation = lazy(() => import("./pages/vendors/create/Create"));
const VendorList = lazy(() => import("./pages/vendors/lists/List"));
const VendorEdit = lazy(() => import("./pages/vendors/edit/Edit"));
const VendorView = lazy(() => import("./pages/vendors/view/View"));
const PurchaseRequestCreate = lazy(
  () => import("./pages/purchase-request/create/Create"),
);
const PurchaseRequestList = lazy(
  () => import("./pages/purchase-request/lists/List"),
);
const PurchaseRequestView = lazy(
  () => import("./pages/purchase-request/view/View"),
);
const PurchaseOrderCreate = lazy(
  () => import("./pages/purchase-order/create/Create"),
);
const PurchaseOrderList = lazy(
  () => import("./pages/purchase-order/lists/List"),
);
const PurchaseOrderView = lazy(
  () => import("./pages/purchase-order/view/View"),
);
const GRNCreate = lazy(() => import("./pages/grn/create/Create"));
const GRNList = lazy(() => import("./pages/grn/lists/List"));
const GRNView = lazy(() => import("./pages/grn/view/View"));
const GeneralExpenseCreate = lazy(
  () => import("./pages/general-expense/create/Create"),
);
const GeneralExpenseList = lazy(
  () => import("./pages/general-expense/lists/List"),
);
const GeneralExpenseView = lazy(
  () => import("./pages/general-expense/view/View"),
);
const ProjectExpenseCreation = lazy(
  () => import("./pages/project-expense/create/Create"),
);
const ProjectExpenseList = lazy(
  () => import("./pages/project-expense/lists/List"),
);
const ProjectExpenseView = lazy(
  () => import("./pages/project-expense/view/View"),
);
const RoleList = lazy(() => import("./pages/role/lists/List"));
const RoleCreate = lazy(() => import("./pages/role/create/Rolecreate"));
const RoleView = lazy(() => import("./pages/role/view/Roleview"));
const EmployeeList = lazy(() => import("./pages/employee/lists/EmployeeList"));
const EmployeeCreation = lazy(
  () => import("./pages/employee/create/EmployeeCreation"),
);
const EmployeeView = lazy(() => import("./pages/employee/view/EmployeeView"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const CashbookReport = lazy(
  () => import("./pages/accounts-report/CashbookReport"),
);
const BankbookReport = lazy(
  () => import("./pages/accounts-report/BankbookReport"),
);
const DaybookReport = lazy(
  () => import("./pages/accounts-report/DaybookReport"),
);
const LedgerReport = lazy(() => import("./pages/accounts-report/LedgerReport"));
const TrialBalanceReport = lazy(
  () => import("./pages/accounts-report/TrialBalance"),
);
const ProfitLossReportPage = lazy(
  () => import("./pages/accounts-report/ProfitLossReport"),
);
const BalanceSheetReport = lazy(
  () => import("./pages/accounts-report/BalanceSheetReport"),
);
const GroupSummary = lazy(() => import("./pages/accounts-report/GroupSummary"));
const ProjectDashboard = lazy(
  () => import("./pages/project-dashboard/ProjectDashboard"),
);
const WorkerAttendanceCreate = lazy(
  () => import("./pages/worker-attendance/create/Create"),
);
const WorkerAttendanceList = lazy(
  () => import("./pages/worker-attendance/lists/Lists"),
);
const WorkerAttendanceView = lazy(
  () => import("./pages/worker-attendance/view/View"),
);
const PurchaseBillCreation = lazy(
  () => import("./pages/purchase-bill/create/Create"),
);
const PurchaseBillList = lazy(() => import("./pages/purchase-bill/lists/List"));
const PurchaseBillView = lazy(() => import("./pages/purchase-bill/view/View"));
const PurchaseBillEdit = lazy(() => import("./pages/purchase-bill/edit/Edit"));
const EmployeeSalaryCreate = lazy(
  () => import("./pages/worker-salary/create/Create"),
);
const EmployeeSalaryList = lazy(
  () => import("./pages/worker-salary/lists/Lists"),
);
const EmployeeSalaryView = lazy(
  () => import("./pages/worker-salary/view/View"),
);
const EmployeeSalaryEdit = lazy(
  () => import("./pages/worker-salary/edit/Edit"),
);
const ProjectProgressView = lazy(
  () => import("./pages/projects/progress/View"),
);
const VendorImport = lazy(() => import("./pages/vendors/lists/import/Import"));
const BudgetCreate = lazy(() => import("./pages/budget/BudgetCreate"));
const BudgetTracking = lazy(() => import("./pages/budget/BudgetTracking"));
const BudgetView = lazy(() => import("./pages/budget/BudgetView"));
const DeletedItemsList = lazy(
  () => import("./pages/deleted-items/DeletedItemsList"),
);
const NotFound = lazy(() => import("./pages/Auth/NotFound"));

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleNavClick = (e) => {
      const navLink = e.target.closest(".navbar-nav a[href]");
      if (navLink && !navLink.hasAttribute("data-bs-toggle")) {
        e.preventDefault();
        e.stopPropagation();
        const href = navLink.getAttribute("href");
        if (href && href.startsWith("/") && href !== "#") {
          navigate(href, { replace: true });
        }
      }
    };
    document.body.addEventListener("click", handleNavClick, true);
    return () =>
      document.body.removeEventListener("click", handleNavClick, true);
  }, [navigate]);

  useEffect(() => {
    const scriptId = "theme-app-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "/assets/js/app.js";
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        if (typeof window.initMenu === "function") {
          window.initMenu();
        }
      };
    }
  }, []);

  return (
    <UserProvider>
      <IdEncoderProvider>
        <GlobalMenuProvider>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/activity-log" element={<ActivityLogPage />} />
              <Route path="/dashboard" element={<Home />} />
              <Route path="/projects/lists" element={<Lists />} />
              <Route path="/projects/view/:id" element={<ProjectView />} />
              <Route path="/projects/create" element={<ProjectCreation />} />
              <Route
                path="/projects/:encodedId/overview"
                element={<ProjectOverview />}
              />
              <Route
                path="/project-labour-mapping/lists"
                element={<ProjectMappingLists />}
              />
              <Route
                path="/project-labour-mapping/create"
                element={<ProjectMappingCreate />}
              />
              <Route
                path="/project-labour-mapping/view/:projectId/:type"
                element={<ProjectMappingViewPage />}
              />
              <Route
                path="/labour-type/create"
                element={<LabourTypeCreate />}
              />
              <Route path="/labour-type/lists" element={<LabourTypeLists />} />
              <Route
                path="/labour-type/view/:id"
                element={<LabourTypeView />}
              />
              <Route
                path="/contractor/create"
                element={<ContractorCreation />}
              />
              <Route path="/contractor/lists" element={<ContractorList />} />
              <Route path="/contractor/view/:id" element={<ContractorView />} />
              <Route
                path="/attendance/create"
                element={<AttendanceCreation />}
              />
              <Route path="/attendance/lists" element={<AttendanceLists />} />
              <Route
                path="/attendance/nmr-create"
                element={<NMRAttendanceCreation />}
              />
              <Route path="/attendance/view/:id" element={<AttendanceView />} />
              <Route
                path="/weekly-statement/create"
                element={<LabourWagesCreation />}
              />
              <Route
                path="/weekly-statement/lists"
                element={<LabourWagesLists />}
              />
              <Route
                path="/weekly-statement/edit/:id"
                element={<LabourWagesCreation />}
              />
              <Route
                path="/weekly-statement/view/:id"
                element={<LabourWagesView />}
              />
              <Route path="/quotation/create" element={<QuotationCreation />} />
              <Route
                path="/quotation/edit/:id"
                element={<QuotationCreation />}
              />
              <Route path="/quotation/lists" element={<QuotationLists />} />
              <Route path="/quotation/view/:id" element={<QuotationView />} />
              <Route path="/customers/create" element={<CustomerCreation />} />
              <Route
                path="/customers/edit/:id"
                element={<CustomerCreation />}
              />
              <Route path="/customers/lists" element={<CustomerLists />} />
              <Route path="/customers/view/:id" element={<CustomerView />} />
              <Route
                path="/estimation/create"
                element={<EstimationCreation />}
              />
              <Route path="/bbs/create/:projectId" element={<BBSCreate />} />
              <Route path="/bbs/view/:id" element={<BBSView />} />
              <Route path="/bbs/edit/:id" element={<BBSEdit />} />
              <Route path="/estimation/lists" element={<EstimationLists />} />
              <Route path="/estimation/edit/:id" element={<EstimationEdit />} />
              <Route path="/estimation/view/:id" element={<EstimationView />} />
              <Route path="/groups/lists" element={<GroupLists />} />
              <Route path="/ledger/create" element={<LedgerCreation />} />
              <Route path="/ledger/edit/:id" element={<LedgerCreation />} />
              <Route path="/ledger/lists" element={<LedgerLists />} />
              <Route path="/ledger/view/:id" element={<LedgerViewPage />} />
              <Route path="/cash-book/create" element={<CashbookCreate />} />
              <Route path="/cash-book/edit/:id" element={<CashbookCreate />} />
              <Route path="/cash-book/lists" element={<CashbookLists />} />
              <Route path="/cash-book/view/:id" element={<CashbookView />} />
              <Route path="/bank-book/create" element={<BankbookCreation />} />
              <Route path="/bank-book/lists" element={<BankbookLists />} />
              <Route
                path="/bank-book/edit/:id"
                element={<BankbookCreation />}
              />
              <Route path="/bank-book/view/:id" element={<BankbookView />} />
              <Route path="/journal/create" element={<JournalCreate />} />
              <Route path="/journal/lists" element={<JournalLists />} />
              <Route path="/journal/view/:id" element={<JournalView />} />
              <Route path="/journal/edit/:id" element={<JournalCreate />} />
              <Route path="/voucher-type/create" element={<VoucherCreate />} />
              <Route
                path="/voucher-type/edit/:id"
                element={<VoucherCreate />}
              />
              <Route path="/voucher-type/lists" element={<VoucherLists />} />
              <Route path="/voucher-type/view/:id" element={<VoucherView />} />
              <Route
                path="/payment-entry/create"
                element={<PaymentCreation />}
              />
              <Route path="/payment-entry/lists" element={<PaymentLists />} />
              <Route
                path="/payment-entry/edit/:id"
                element={<PaymentCreation />}
              />
              <Route path="/payment-entry/view/:id" element={<PaymentView />} />
              <Route path="/lookup/lists" element={<LookupMasterList />} />
              <Route path="/lookup/:id/values" element={<LookupValueList />} />
              <Route path="/receipt/create" element={<ReceiptCreate />} />
              <Route path="/receipt/lists" element={<ReceiptLists />} />
              <Route path="/receipt/edit/:id" element={<ReceiptCreate />} />
              <Route path="/receipt/view/:id" element={<ReceiptView />} />
              <Route path="/invoice/create" element={<InvoiceCreation />} />
              <Route path="/invoice/create/:id" element={<InvoiceCreation />} />
              <Route path="/invoice/lists" element={<InvoiceLists />} />
              <Route path="/invoice/edit/:id" element={<InvoiceCreation />} />
              <Route path="/invoice/view/:id" element={<InvoiceView />} />

              <Route
                path="/product-category/create"
                element={<ProductCategoryCreation />}
              />
              <Route
                path="/product-category/lists"
                element={<ProductCategoryList />}
              />
              <Route
                path="/product-category/view/:id"
                element={<ProductCategoryView />}
              />
              <Route path="/uom/create" element={<UomCreate />} />
              <Route path="/uom/lists" element={<UomList />} />
              <Route path="/uom/view/:id" element={<UomView />} />
              <Route path="/products/create" element={<ProductCreation />} />
              <Route
                path="/products/create/category/:categoryId"
                element={<ProductCreation />}
              />
              <Route path="/products/lists" element={<ProductList />} />
              <Route path="/products/view/:id" element={<ProductView />} />
              <Route path="/vendors/create" element={<VendorCreation />} />
              <Route path="/vendors/lists" element={<VendorList />} />
              <Route path="/vendors/edit/:id" element={<VendorEdit />} />
              <Route path="/vendors/view/:id" element={<VendorView />} />
              <Route
                path="/purchase-request/create"
                element={<PurchaseRequestCreate />}
              />
              <Route
                path="/purchase-request/lists"
                element={<PurchaseRequestList />}
              />
              <Route
                path="/purchase-request/view/:id"
                element={<PurchaseRequestView />}
              />
              <Route
                path="/purchase-order/create/:purchaseRequestId"
                element={<PurchaseOrderCreate />}
              />
              <Route
                path="/purchase-order/create"
                element={<PurchaseOrderCreate />}
              />
              <Route
                path="/purchase-order/lists"
                element={<PurchaseOrderList />}
              />
              <Route
                path="/purchase-order/view/:id"
                element={<PurchaseOrderView />}
              />
              <Route path="/grn/create" element={<GRNCreate />} />
              <Route path="/grn/create/:orderId" element={<GRNCreate />} />
              <Route path="/grn/lists" element={<GRNList />} />
              <Route path="/grn/view/:id" element={<GRNView />} />
              <Route
                path="/purchase-bill/create"
                element={<PurchaseBillCreation />}
              />
              <Route
                path="/purchase-bill/create/:grnId?"
                element={<PurchaseBillCreation />}
              />
              <Route
                path="/purchase-bill/lists"
                element={<PurchaseBillList />}
              />
              <Route
                path="/purchase-bill/view/:id"
                element={<PurchaseBillView />}
              />
              <Route
                path="/purchase-bill/edit/:id"
                element={<PurchaseBillCreation />}
              />
              <Route
                path="/general-expense/create"
                element={<GeneralExpenseCreate />}
              />
              <Route
                path="/general-expense/lists"
                element={<GeneralExpenseList />}
              />
              <Route
                path="/general-expense/view/:id"
                element={<GeneralExpenseView />}
              />
              <Route path="/employee/lists" element={<EmployeeList />} />
              <Route path="/employee/create" element={<EmployeeCreation />} />
              <Route path="/employee/edit/:id" element={<EmployeeCreation />} />
              <Route path="/employee/view/:id" element={<EmployeeView />} />
              <Route path="/roles/lists" element={<RoleList />} />
              <Route path="/roles/create" element={<RoleCreate />} />
              <Route path="/roles/view/:id" element={<RoleView />} />
              <Route path="/roles/edit/:id" element={<RoleCreate />} />
              <Route path="/profile" element={<Profile />} />
              <Route
                path="/accounts-report/cash-book"
                element={<CashbookReport />}
              />
              <Route
                path="/accounts-report/bank-book"
                element={<BankbookReport />}
              />
              <Route
                path="/accounts-report/day-book"
                element={<DaybookReport />}
              />
              <Route
                path="/accounts-report/ledger-report"
                element={<LedgerReport />}
              />
              <Route
                path="/accounts-report/trial-balance-report"
                element={<TrialBalanceReport />}
              />
              <Route
                path="/accounts-report/balance-sheet-report"
                element={<BalanceSheetReport />}
              />
              <Route
                path="/accounts-report/profit-loss-report"
                element={<ProfitLossReportPage />}
              />
              <Route
                path="/accounts-report/group-summary/"
                element={<GroupSummary />}
              />
              <Route
                path="/accounts-report/group-summary/:groupName"
                element={<GroupSummary />}
              />
              <Route path="/projects/report" element={<ProjectDashboard />} />
              <Route
                path="/worker-attendance/create"
                element={<WorkerAttendanceCreate />}
              />
              <Route
                path="/worker-attendance/lists"
                element={<WorkerAttendanceList />}
              />
              <Route
                path="/worker-attendance/view/:id"
                element={<WorkerAttendanceView />}
              />
              <Route
                path="/project-expense/create"
                element={<ProjectExpenseCreation />}
              />
              <Route
                path="/project-expense/lists"
                element={<ProjectExpenseList />}
              />
              <Route
                path="/project-expense/view/:id"
                element={<ProjectExpenseView />}
              />
              <Route
                path="/worker-salary/view/:id"
                element={<EmployeeSalaryView />}
              />
              <Route
                path="/worker-salary/create"
                element={<EmployeeSalaryCreate />}
              />
              <Route
                path="/worker-salary/lists"
                element={<EmployeeSalaryList />}
              />
              <Route
                path="/worker-salary/edit/:id"
                element={<EmployeeSalaryEdit />}
              />
              <Route
                path="/projects/progress/:id"
                element={<ProjectProgressView />}
              />
              <Route path="/budget/create" element={<BudgetCreate />} />
              <Route path="/budget/tracking" element={<BudgetTracking />} />
              <Route path="/vendor/lists/import" element={<VendorImport />} />
              <Route path="/budget/view/:id" element={<BudgetView />} />
              <Route path="/deleted-items" element={<DeletedItemsList />} />
            </Route>

            {/* Catch-all 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </GlobalMenuProvider>
      </IdEncoderProvider>
    </UserProvider>
  );
}

export default App;
